/**
 * useChatStream Core (Shared)
 *
 * Platform-agnostic SSE streaming logic for the AI Chat Assistant.
 * Both web and mobile provide thin wrappers that inject:
 * - apiFetch: platform auth (cookies vs Bearer token)
 * - onNavigate: platform navigation (react-router vs react-navigation)
 * - onPreferenceUpdate: platform preference store
 * - getChatStore: platform chat store getState()
 * - isStreaming: reactive subscription from platform store
 */

import { useCallback, useRef } from 'react';
import type { ChatInitContext, ChatInitResponse, ChatMessage, ChatStreamEvent } from '../chatTypes';
import type { ChatState } from '../stores/chatStoreCore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseChatStreamResult {
  sendMessage: (message: string, initContext?: ChatInitContext) => Promise<void>;
  initChat: (initContext?: ChatInitContext) => Promise<ChatInitResponse | null>;
  confirmAction: (confirmationId: string, confirmed: boolean) => Promise<void>;
  cancel: () => void;
  isStreaming: boolean;
}

export interface UseChatStreamDeps {
  /** Platform-specific fetch wrapper */
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** Handle navigation events from the AI chat */
  onNavigate: (screen: string, context?: Record<string, unknown>) => void;
  /** Handle preference updates from the AI chat */
  onPreferenceUpdate: (key: string, value: string) => void;
  /** Get fresh chat store state (both platforms use getState()) */
  getChatStore: () => ChatState;
  /** Reactive isStreaming value from platform store subscription */
  isStreaming: boolean;
}

// ---------------------------------------------------------------------------
// Utilities (pure, no React)
// ---------------------------------------------------------------------------

export function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function getFriendlyError(status: number, serverMessage?: string): string {
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in again to continue chatting.';
    case 403:
      return serverMessage?.includes('Elite')
        ? 'AI Chat is available on the Elite plan. Upgrade to unlock this feature!'
        : "You don't have access to this feature right now.";
    case 429:
      return "You're sending messages too fast — take a breath and try again in a moment.";
    default:
      return 'Something went wrong. Please try again.';
  }
}

// ---------------------------------------------------------------------------
// Shared hook
// ---------------------------------------------------------------------------

export function useChatStreamCore(deps: UseChatStreamDeps): UseChatStreamResult {
  const { apiFetch, onNavigate, onPreferenceUpdate, getChatStore, isStreaming } = deps;

  const abortRef = useRef<AbortController | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable callback using getChatStore()
  const handleStreamEvent = useCallback((event: ChatStreamEvent) => {
    const store = getChatStore();

    switch (event.type) {
      case 'text_delta':
        store.appendToLastMessage(event.text);
        break;

      case 'tool_start':
        store.addToolCallToLastMessage({
          id: event.toolCallId,
          name: event.toolName,
          status: 'running',
          startedAt: Date.now(),
        });
        break;

      case 'tool_result':
        store.updateToolCallStatus(
          event.toolCallId,
          event.isError ? 'error' : 'complete',
          event.errorCategory,
        );
        if (event.dataCard) {
          store.addDataCardToLastMessage(event.dataCard);
        }
        break;

      case 'data_card':
        store.addDataCardToLastMessage(event.card);
        break;

      case 'navigate':
        onNavigate(event.screen, event.context);
        break;

      case 'preference_update':
        onPreferenceUpdate(event.key, event.value);
        break;

      case 'clear_chat':
        store.clearConversation();
        store.setStreaming(true);
        store.addMessage({
          id: generateMessageId(),
          role: 'assistant',
          content: '',
          timestamp: Date.now(),
        });
        break;

      case 'confirmation':
        store.setPendingConfirmation({
          confirmationId: event.confirmationId,
          action: event.action,
          details: event.details,
          structuredDetails: event.structuredDetails,
          toolCallId: event.toolCallId,
        });
        break;

      case 'pending_followup':
        // Server is about to stream brief follow-up text while the
        // confirmation modal is open. UI shows a "continuing while
        // waiting for confirmation" indicator alongside the modal.
        store.setPendingFollowupActive(true);
        break;

      case 'chips':
        store.setChips(event.chips);
        break;

      case 'compaction':
        store.applyCompaction(event.summary, event.removedCount);
        break;

      case 'conversation_saved':
        store.setConversationId(event.conversationId);
        break;

      case 'done':
        store.finalizeRunningToolCalls('complete');
        store.setStreaming(false);
        store.setPendingFollowupActive(false);
        store.incrementUnread();
        break;

      case 'error':
        store.finalizeRunningToolCalls('error');
        store.appendToLastMessage(event.message || 'Something went wrong. Please try again.');
        store.setStreaming(false);
        store.setPendingFollowupActive(false);
        break;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleStreamEvent is stable (empty deps)
  const parseSSEStream = useCallback(
    async (response: Response, isCurrent: () => boolean) => {
      let buffer = '';

      const processBuffer = () => {
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!isCurrent()) return;
          const lines = part.split('\n');
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            let event: ChatStreamEvent;
            try {
              event = JSON.parse(line.slice(6));
            } catch (e) {
              console.warn('[useChatStream] Failed to parse SSE event:', line.slice(6, 100), e);
              continue;
            }

            handleStreamEvent(event);
          }
        }
      };

      // A fetch without a readable body (React Native's built-in one) still has the whole
      // reply once it resolves; returning here left the assistant message empty.
      if (!response.body) {
        buffer = `${await response.text()}\n\n`;
        processBuffer();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        const STREAM_TIMEOUT_MS = 60000;

        while (true) {
          const timeoutId = setTimeout(() => {
            console.warn('[useChatStream] Stream read timed out after 60s of inactivity');
            reader.cancel().catch(() => {});
          }, STREAM_TIMEOUT_MS);

          let done: boolean;
          let value: Uint8Array | undefined;
          try {
            ({ done, value } = await reader.read());
          } finally {
            clearTimeout(timeoutId);
          }

          if (done || !isCurrent()) break;

          buffer += decoder.decode(value, { stream: true });
          processBuffer();
        }

        // Process any remaining data in the buffer after stream closes
        if (buffer.trim()) {
          buffer += '\n\n';
          processBuffer();
        }
      } finally {
        reader.releaseLock();
      }
    },
    [handleStreamEvent],
  );

  /** Build conversation history from current messages (exclude trailing empty assistant) */
  const buildConversationHistory = () => {
    const currentMessages = getChatStore().messages;
    return currentMessages
      .slice(0, -1)
      .filter((m) => m.content || (m.dataCards && m.dataCards.length > 0));
  };

  /** Handle response errors — appends friendly message and stops streaming */
  const handleResponseError = async (response: Response, isCurrent: () => boolean) => {
    const data = await response.json().catch(() => ({}));
    if (!isCurrent()) return;
    getChatStore().appendToLastMessage(getFriendlyError(response.status, data.error));
    getChatStore().setStreaming(false);
  };

  /** Safety net: if stream ended without a 'done' event, ensure we stop streaming */
  const ensureStreamingEnded = () => {
    if (getChatStore().isStreaming) {
      getChatStore().finalizeRunningToolCalls('complete');
      getChatStore().setStreaming(false);
    }
  };

  /** Register the request so resetLocal can abort it; the check turns false once a reset has run. */
  const startStream = (controller: AbortController): (() => boolean) => {
    const epoch = getChatStore().sessionEpoch;
    getChatStore().setActiveStreamAbort(controller);
    return () => getChatStore().sessionEpoch === epoch;
  };

  const endStream = (controller: AbortController) => {
    if (getChatStore().activeStreamAbort === controller) getChatStore().setActiveStreamAbort(null);
  };

  const sendMessage = useCallback(
    async (message: string, initContext?: ChatInitContext) => {
      const store = getChatStore();
      if (store.isStreaming) return;

      // Dismiss any pending confirmation — user chose to send a message instead
      if (store.pendingConfirmation) {
        store.setPendingConfirmation(null);
      }

      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: message,
        timestamp: Date.now(),
      };
      store.addMessage(userMessage);

      const assistantMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
      };
      store.addMessage(assistantMessage);

      store.setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      const isCurrent = startStream(controller);

      try {
        const conversationHistory = buildConversationHistory();

        const response = await apiFetch('/api/chat/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            conversationHistory,
            initContext,
            conversationId: getChatStore().conversationId ?? undefined,
          }),
          signal: controller.signal,
        });
        if (!isCurrent()) return;

        if (!response.ok) {
          await handleResponseError(response, isCurrent);
          return;
        }

        await parseSSEStream(response, isCurrent);
        if (isCurrent()) ensureStreamingEnded();
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof Error && error.name === 'AbortError') {
          getChatStore().finalizeRunningToolCalls('error');
          getChatStore().setStreaming(false);
          return;
        }
        getChatStore().finalizeRunningToolCalls('error');
        getChatStore().appendToLastMessage('Connection lost. Please try again.');
        getChatStore().setStreaming(false);
      } finally {
        abortRef.current = null;
        endStream(controller);
      }
    },
    [parseSSEStream],
  );

  const initChat = useCallback(
    async (initContext?: ChatInitContext): Promise<ChatInitResponse | null> => {
      const epoch = getChatStore().sessionEpoch;
      try {
        const response = await apiFetch('/api/chat/init', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ initContext }),
        });

        if (!response.ok) return null;

        const data: ChatInitResponse = await response.json();
        if (getChatStore().sessionEpoch !== epoch) return null;

        const store = getChatStore();
        store.addMessage({
          id: generateMessageId(),
          role: 'assistant',
          content: data.welcomeMessage,
          timestamp: Date.now(),
        });
        store.setChips(data.chips);

        return data;
      } catch {
        return null;
      }
    },
    [],
  );

  const confirmAction = useCallback(
    async (confirmationId: string, confirmed: boolean) => {
      const store = getChatStore();
      store.setPendingConfirmation(null);
      store.setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      const isCurrent = startStream(controller);

      store.addMessage({
        id: generateMessageId(),
        role: 'user',
        content: confirmed ? 'Confirmed' : 'Cancelled',
        timestamp: Date.now(),
      });

      store.addMessage({
        id: generateMessageId(),
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
      });

      try {
        const conversationHistory = buildConversationHistory();

        const response = await apiFetch('/api/chat/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            confirmationId,
            confirmed,
            conversationHistory,
            conversationId: getChatStore().conversationId ?? undefined,
          }),
          signal: controller.signal,
        });
        if (!isCurrent()) return;

        if (response.ok) {
          await parseSSEStream(response, isCurrent);
          if (isCurrent()) ensureStreamingEnded();
        } else {
          const data = await response.json().catch(() => ({}));
          if (!isCurrent()) return;
          getChatStore().finalizeRunningToolCalls('error');
          getChatStore().appendToLastMessage(getFriendlyError(response.status, data.error));
          getChatStore().setStreaming(false);
        }
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof Error && error.name === 'AbortError') {
          getChatStore().finalizeRunningToolCalls('error');
          getChatStore().setStreaming(false);
          return;
        }
        getChatStore().finalizeRunningToolCalls('error');
        getChatStore().appendToLastMessage('Connection lost.');
        getChatStore().setStreaming(false);
      } finally {
        abortRef.current = null;
        endStream(controller);
      }
    },
    [parseSSEStream],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    getChatStore().finalizeRunningToolCalls('error');
    getChatStore().setStreaming(false);
  }, []);

  return {
    sendMessage,
    initChat,
    confirmAction,
    cancel,
    isStreaming,
  };
}
